import { PDFDocument, StandardFonts, rgb, type PDFPage } from "pdf-lib";
import type {
  GSCReportData,
  AgencyBrandConfig,
  GSCDailyRow,
  GSCQueryRow,
} from "@agencylens/shared";
import {
  MARGIN_X,
  PAGE_H,
  PAGE_W,
  BODY,
  drawHeader,
  drawFooter,
  drawKpiGrid,
  drawTable,
  drawFallbackLineChart,
  fmtDateRange,
  fmtNumber,
  fmtPercent,
  fmtPosition,
  type PDFFonts,
  type TableColumn,
} from "./render-utils.js";

export interface RenderGSCOptions {
  data: GSCReportData;
  agency: AgencyBrandConfig;
  /** Optional pre-rasterized chart PNG (Recharts → canvas). */
  chartPng?: Uint8Array;
}

/* ---------------------------------------------------- summary page */

function drawSummaryPage(
  page: PDFPage,
  fonts: PDFFonts,
  data: GSCReportData,
  brand: AgencyBrandConfig,
): void {
  drawHeader(page, fonts, brand, "Search Console Report");

  let y = PAGE_H - 120;
  page.drawText(data.site.siteUrl, {
    x: MARGIN_X,
    y,
    size: 14,
    font: fonts.bold,
    color: rgb(0.2, 0.2, 0.2),
  });
  y -= 18;
  page.drawText(fmtDateRange(data.startDate, data.endDate), {
    x: MARGIN_X,
    y,
    size: 10,
    font: fonts.regular,
    color: rgb(0.45, 0.45, 0.45),
  });
  y -= 32;

  drawKpiGrid(
    page,
    fonts,
    [
      { label: "Clicks", value: fmtNumber(data.totals.clicks) },
      { label: "Impressions", value: fmtNumber(data.totals.impressions) },
      { label: "CTR", value: fmtPercent(data.totals.ctr) },
      { label: "Avg. position", value: fmtPosition(data.totals.position) },
    ],
    y,
  );
}

/* ------------------------------------------------------- chart page */

async function drawChartPage(
  doc: PDFDocument,
  page: PDFPage,
  fonts: PDFFonts,
  data: GSCReportData,
  brand: AgencyBrandConfig,
  chartPng?: Uint8Array,
): Promise<void> {
  drawHeader(page, fonts, brand, "Search Console Report");
  page.drawText("Clicks over time", {
    x: MARGIN_X,
    y: PAGE_H - 120,
    size: 14,
    font: fonts.bold,
    color: rgb(0.2, 0.2, 0.2),
  });

  if (chartPng) {
    const png = await doc.embedPng(chartPng);
    const maxW = PAGE_W - 2 * MARGIN_X;
    const maxH = BODY - 160;
    const aspect = png.width / png.height;
    let w = maxW;
    let h = w / aspect;
    if (h > maxH) {
      h = maxH;
      w = h * aspect;
    }
    page.drawImage(png, {
      x: MARGIN_X + (maxW - w) / 2,
      y: PAGE_H - 140 - h,
      width: w,
      height: h,
    });
    return;
  }

  drawFallbackLineChart(
    page,
    fonts,
    brand,
    data.daily.map((d) => ({ date: d.date, value: d.clicks })),
    { topY: PAGE_H - 150 },
  );
}

/* ---------------------------------------------------- daily table page */

const GSC_DAILY_COLUMNS: TableColumn<GSCDailyRow>[] = [
  { label: "Date", width: 80, format: (r) => r.date },
  { label: "Clicks", width: 80, align: "right", format: (r) => fmtNumber(r.clicks) },
  {
    label: "Impressions",
    width: 100,
    align: "right",
    format: (r) => fmtNumber(r.impressions),
  },
  { label: "CTR", width: 70, align: "right", format: (r) => fmtPercent(r.ctr) },
  { label: "Position", width: 80, align: "right", format: (r) => fmtPosition(r.position) },
];

function drawDailyTablePage(
  page: PDFPage,
  fonts: PDFFonts,
  rows: GSCDailyRow[],
  brand: AgencyBrandConfig,
  pageLabel: string,
): void {
  drawHeader(page, fonts, brand, "Search Console Report");
  page.drawText(pageLabel, {
    x: MARGIN_X,
    y: PAGE_H - 120,
    size: 14,
    font: fonts.bold,
    color: rgb(0.2, 0.2, 0.2),
  });
  drawTable(page, fonts, brand, GSC_DAILY_COLUMNS, rows, PAGE_H - 150);
}

/* ---------------------------------------------------- top-queries page */

const GSC_QUERY_COLUMNS: TableColumn<GSCQueryRow>[] = [
  { label: "Query", width: 220, format: (r) => truncate(r.query, 40) },
  { label: "Clicks", width: 70, align: "right", format: (r) => fmtNumber(r.clicks) },
  {
    label: "Impressions",
    width: 90,
    align: "right",
    format: (r) => fmtNumber(r.impressions),
  },
  { label: "CTR", width: 60, align: "right", format: (r) => fmtPercent(r.ctr) },
  { label: "Position", width: 70, align: "right", format: (r) => fmtPosition(r.position) },
];

function truncate(s: string, maxLen: number): string {
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen - 1) + "…";
}

function drawQueriesPage(
  page: PDFPage,
  fonts: PDFFonts,
  rows: GSCQueryRow[],
  brand: AgencyBrandConfig,
): void {
  drawHeader(page, fonts, brand, "Search Console Report");
  page.drawText("Top search queries", {
    x: MARGIN_X,
    y: PAGE_H - 120,
    size: 14,
    font: fonts.bold,
    color: rgb(0.2, 0.2, 0.2),
  });
  drawTable(page, fonts, brand, GSC_QUERY_COLUMNS, rows, PAGE_H - 150);
}

/* --------------------------------------------------------- main render */

const ROWS_PER_TABLE_PAGE = 31;

/**
 * Render a Google Search Console PDF report.
 *
 * Layout:
 *   Page 1 — summary stat cards (clicks, impressions, CTR, avg position)
 *   Page 2 — clicks-over-time chart (PNG if provided, fallback line if not)
 *   Page 3 — top search queries table (up to 25)
 *   Page 4+ — daily breakdown table (paginated)
 */
export async function renderGSCReport(
  opts: RenderGSCOptions,
): Promise<Uint8Array> {
  const { data, agency, chartPng } = opts;
  const doc = await PDFDocument.create();
  doc.setTitle(`${agency.agencyName} — ${data.site.siteUrl}`);
  doc.setAuthor(agency.agencyName);
  doc.setProducer("AgencyLens");
  doc.setCreator("AgencyLens");
  doc.setCreationDate(new Date());

  const regular = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const fonts: PDFFonts = { regular, bold };

  // Page 1 — summary
  const summary = doc.addPage([PAGE_W, PAGE_H]);
  drawSummaryPage(summary, fonts, data, agency);

  // Page 2 — chart
  const chartPage = doc.addPage([PAGE_W, PAGE_H]);
  await drawChartPage(doc, chartPage, fonts, data, agency, chartPng);

  // Page 3 — queries
  const queriesPage = doc.addPage([PAGE_W, PAGE_H]);
  drawQueriesPage(queriesPage, fonts, data.topQueries.slice(0, 25), agency);

  // Page 4+ — daily breakdown
  const chunks: GSCDailyRow[][] = [];
  for (let i = 0; i < data.daily.length; i += ROWS_PER_TABLE_PAGE) {
    chunks.push(data.daily.slice(i, i + ROWS_PER_TABLE_PAGE));
  }
  if (chunks.length === 0) chunks.push([]);

  chunks.forEach((chunk, idx) => {
    const page = doc.addPage([PAGE_W, PAGE_H]);
    const label =
      chunks.length === 1
        ? "Daily breakdown"
        : `Daily breakdown (${idx + 1} of ${chunks.length})`;
    drawDailyTablePage(page, fonts, chunk, agency, label);
  });

  // Footers
  const pages = doc.getPages();
  const total = pages.length;
  pages.forEach((p, i) => drawFooter(p, fonts, agency, i + 1, total));

  return doc.save();
}
